import { useEffect, useState } from "react";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import api, { imgUrl, formatApiErrorDetail } from "@/lib/api";
import { ImageUploader } from "./ImageUploader";

const CATEGORIES = ["Rooms", "Lobby & Reception", "Neighborhood"];

export const AdminGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [pending, setPending] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Rooms");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/gallery").then(({ data }) => setPhotos(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!pending.length) { toast.error("Upload at least one photo first"); return; }
    setSaving(true);
    try {
      for (const url of pending) await api.post("/gallery", { url, title: title || "Hotel Vijayshree Inn", category });
      toast.success(`${pending.length} photo${pending.length > 1 ? "s" : ""} added to gallery`);
      setPending([]); setTitle("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Photo removed");
    load();
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5 lg:p-6" data-testid="admin-gallery-section">
      <h2 className="font-serif text-xl font-semibold">Gallery Photos</h2>
      <p className="text-sm text-muted-foreground mt-1">Upload real photos of your hotel. They appear instantly in the website gallery.</p>

      <div className="mt-4 grid lg:grid-cols-[1fr_280px] gap-4">
        <ImageUploader images={pending} onChange={setPending} testId="gallery-uploader" />
        <div className="space-y-3">
          <div><Label className="text-xs">Caption</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Deluxe Room Interior" data-testid="gallery-title-input" /></div>
          <div><Label className="text-xs">Category</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-category-select"
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <Button onClick={save} disabled={saving || !pending.length} className="w-full gap-1.5 bg-primary hover:bg-primary/90" data-testid="gallery-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to Gallery ({pending.length})
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {photos.map((p) => (
          <div key={p.id} className="group relative aspect-square rounded-lg overflow-hidden border border-border" data-testid={`gallery-admin-item-${p.id}`}>
            <img src={imgUrl(p.url)} alt={p.title} className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate">{p.title}</div>
            <button onClick={() => remove(p.id)} data-testid={`gallery-delete-${p.id}`}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
    </section>
  );
};
